import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DateTime } from "luxon";

import { Button } from "../../components/Buttons/Button";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import { TimePicker } from "../../components/TimePicker/TimePicker";
import adjustTimeForMidnightCross from "../../utilities/helpers/adjustTimeForMidnightCross";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { plantAjax } from "../../ajax/PlantAjax";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { useSnackbar } from "notistack";
import { PlantJobTypeDTO } from "../../DTOs/PlantJobTypeDTO";
import { StoppagePreset } from "../../DTOs/Stoppage/MultipleStoppages";
import { PlantDTO } from "../../DTOs/Plants/PlantDTO";
import { TripleShift } from "../../types/Shift";
import { queryClient } from "../../queryClient";
import {
  PLANT_JOB_TYPES_KEY,
  addMultipleStoppagesOffline,
  availableMachinesFromCache,
} from "../../offline/multipleStoppagesOffline";
import { isOnlineNow } from "../../offline/connectivity";

interface MultipleStoppagesModalProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: {
    value: number,
    label: string,
    groupColor: string,
    stoppageType: string,
    downtimeTypeId: number
  }[];
  closeModal: () => void;
  refreshPage: () => void;
}

type FilterState = {
  stoppageTypeId: number;
  stoppageSubTypeId: number;
  timeIn: DateTime;
  timeOut?: DateTime | null;
  jobTypes?: PlantJobTypeDTO[];
  machineIds: number[];
}

const MultipleStoppagesModal = (
  {
    selectedDate,
    selectedShift,
    stoppageTypeGroupOptions,
    closeModal,
    refreshPage
  }: MultipleStoppagesModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const {shiftStartTime: rawStart, shiftEndTime: rawEnd} = useShiftTimes();
  // TODO: this is a shitty workaround for the afternoon shift addition
  const shiftStartTime = selectedShift[0] === "A"
    ? DateTime.fromObject({ hour: 14, minute: 0 }, { zone: rawStart.zone })
    : rawStart;

  const shiftEndTime = selectedShift[0] === "A"
    ? DateTime.fromObject({ hour: 22, minute: 0 }, { zone: rawEnd.zone })
    : rawEnd;
  // Note: the picker only chooses a time - the date it comes back with is whatever MUI used as its
  // reference, i.e. today. Every other TimePicker in the app puts the picked time onto the selected
  // shift's date through this helper, and this modal did not: working a shift that is not today (any
  // night shift after midnight, or a shift created offline) stamped the stoppage with today's date.
  // A Time Out a day past the shift end reads as an open stoppage, so every machine in the batch
  // came back down for the whole shift.
  const onShiftDate = (picked?: DateTime | null): DateTime | undefined => {
    if (!picked || !DateTime.isDateTime(picked)) return undefined;
    return adjustTimeForMidnightCross(picked, selectedDate, shiftStartTime, shiftEndTime);
  };

  const [stoppagePresets, setStoppagePresets] = useState<StoppagePreset[]>([]);
  const [plantJobTypes, setPlantJobTypes] = useState<PlantJobTypeDTO[]>([]);
  const [plants, setPlants] = useState<PlantDTO[]>([]);
  const [filterState, setFilterState] = useState<FilterState>({
    stoppageTypeId: 0,
    stoppageSubTypeId: 0,
    timeIn: shiftStartTime,
    timeOut: null,
    machineIds: [],
  });

  useEffect(() => {
    // todo: workaround
    if (selectedShift === "AFTERNOON") {
      setStoppagePresets([]);
      return;
    }
    stoppagesAjax.getStoppagePresets(selectedDate, selectedShift)
      .then(res => {
        if (res instanceof ErrorResponse || !res) {
          setStoppagePresets([]);
        } else {
          setStoppagePresets(res);
        }
      })
      .catch(_ => {
        setStoppagePresets([]);
      });
  }, []);

  // Note: cached under a query key so the list is still there offline (see
  // usePrefetchCriticalPages, which warms it).
  useEffect(() => {
    queryClient.fetchQuery({
      queryKey: PLANT_JOB_TYPES_KEY,
      queryFn: async () => {
        const res = await plantAjax.getPlantJobTypes();
        // Note: throw rather than return an empty list. A failed read used to resolve with [], which
        // React Query cached as the truth for the next half hour and persisted to IndexedDB - so one
        // attempt to open this modal offline left the job types filter permanently blank, even after
        // reconnecting. Throwing keeps whatever the prefetch cached and lets the catch below use it.
        if (res instanceof ErrorResponse) throw new Error("Failed to load plant job types");
        return res.data;
      },
      staleTime: 30 * 60 * 1000,
    })
      .then(types => setPlantJobTypes(types ?? []))
      .catch(() => setPlantJobTypes(queryClient.getQueryData<PlantJobTypeDTO[]>(PLANT_JOB_TYPES_KEY) ?? []));
  }, []);

  useEffect(() => {
    if (filterState.stoppageSubTypeId === 0 || filterState.stoppageTypeId === 0 || filterState.timeIn === undefined) {
      setPlants([]);
      return;
    }
    const jobTypesEnum = filterState.jobTypes && filterState.jobTypes.length > 0
      ? filterState.jobTypes.reduce((acc, jt) => acc | jt.value, 0)
      : undefined

    // Note: offline the server cannot work out which machines are available, so take the ones the
    // cached operations say are up. Job types are not applied - a cached operation row does not carry
    // the plant's job types - so the list is everything currently up.
    if (!isOnlineNow()) {
      const available = availableMachinesFromCache(selectedDate, selectedShift, jobTypesEnum);
      setPlants(available);
      setFilterState(prev => ({ ...prev, machineIds: selectionFor(available, prev.machineIds) }));
      return;
    }

    plantAjax.getMultipleStoppagePlants(selectedDate, selectedShift, filterState.timeIn, filterState.timeOut ?? undefined, jobTypesEnum)
      .then(res => {
        if (!res.success || !res.data) {
          setPlants([]);
        } else {
          setPlants(res.data);
          setFilterState(prev => ({
            ...prev,
            machineIds: selectionFor(res.data!, prev.machineIds)
          }));
        }
      })
      .catch(_ => {
        setPlants([]);
      });
  }, [filterState.timeIn, filterState.jobTypes, filterState.stoppageTypeId, filterState.stoppageSubTypeId]);

  // Note: once the crew has chosen machines by hand, the list refreshing must not silently choose for
  // them again. Changing the job type (or the stoppage type) re-runs the effect below, which used to
  // reset the selection to every available machine - so a crew who deselected all, ticked three
  // machines and then set a job type had the stoppage written against the whole fleet, with only the
  // re-ticked boxes as a clue.
  const hasChosenMachines = useRef(false);

  const toggleSelection = (id: number) => {
    hasChosenMachines.current = true;
    setFilterState(prev => {
        return {
          ...prev,
          machineIds: prev.machineIds.includes(id)
            ? prev.machineIds.filter(x => x !== id)
            : [...prev.machineIds, id]
        }
      }
    );
  };

  // Note: every machine by default, but a selection the crew made by hand survives the list changing -
  // narrowed to the machines still on it.
  const selectionFor = (available: PlantDTO[], chosen: number[]): number[] => {
    const availableIds = available.map(machine => machine.id);
    if (!hasChosenMachines.current) return availableIds;
    return chosen.filter(id => availableIds.includes(id));
  };

  const addMultipleStoppages = async () => {
    if (filterState.stoppageSubTypeId === 0 || filterState.stoppageTypeId === 0) {
      enqueueSnackbar('Stoppage type must be selected', { variant: 'error', autoHideDuration: 3000 });
    }

    if (filterState.machineIds.length === 0) {
      enqueueSnackbar('Plants must be selected', { variant: 'error', autoHideDuration: 3000 });
    }

    setIsLoading(true);
    try {
      // Note: offline there is no batch endpoint to queue against, so fan the batch out into the
      // ordinary queued stoppage creates - one per machine - and patch the same caches a single
      // offline stoppage does. The sync then applies them individually like any other create.
      if (!isOnlineNow()) {
        const added = await addMultipleStoppagesOffline({
          machineIds: filterState.machineIds,
          machines: plants,
          stoppageTypeId: filterState.stoppageTypeId,
          stoppageSubTypeId: filterState.stoppageSubTypeId,
          timeIn: filterState.timeIn,
          timeOut: filterState.timeOut ?? undefined,
          subType: stoppageTypeGroupOptions.find(o => o.value === filterState.stoppageSubTypeId),
          selectedDate,
          selectedShift,
        });

        enqueueSnackbar(`${added} stoppages saved offline; they will sync when you reconnect`, { variant: 'info', autoHideDuration: 5000 })
        closeModal();
        refreshPage();
        return true;
      }

      const response = await stoppagesAjax.insertMultipleStoppages({
        stoppageTypeId: filterState.stoppageTypeId,
        stoppageSubTypeId: filterState.stoppageSubTypeId,
        timeIn: filterState.timeIn,
        timeOut: filterState.timeOut ?? undefined,
        machineIds: filterState.machineIds
      });

      if (!response.success && !response.data) {
        response.errors?.forEach((error, index) => {
          enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
        })

        return false;
      }

      enqueueSnackbar("Multiple stoppages added successfully", { variant: 'success', autoHideDuration: 5000 })
      closeModal();
      refreshPage();

    } catch (error) {
      let message = "Error adding multiple stoppages";

      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === "string") {
        message = error;
      }

      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 })
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form className="space-y-6 text-theme-text">
      <h2 className="text-2xl pb-2 font-semibold border-b border-theme-border">Create Multiple Stoppages</h2>

      <div className="flex flex-wrap gap-2">
        {stoppagePresets.map(preset => (
          <button
            key={preset.name}
            type="button"
            className="px-3 py-1 rounded bg-theme-info text-theme-info-muted font-medium hover:bg-theme-info-hover transition-colors"
            onClick={() =>
              setFilterState(prev => ({
                ...prev,
                timeIn: preset.timeIn,
                timeOut: preset.timeOut,
                stoppageSubTypeId: preset.stoppageSubTypeId,
                stoppageTypeId: preset.stoppageTypeId,
                machineIds: plants.map(p => p.id)
              }))
            }
          >
            {preset.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-medium mb-1 block">Stoppage Type</label>
          <Autocomplete
            size="small"
            options={stoppageTypeGroupOptions}
            value={stoppageTypeGroupOptions.find(o => o.value === filterState.stoppageSubTypeId) ?? null}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            groupBy={(option) => option.stoppageType}
            renderGroup={(params) => {
              const groupColor = stoppageTypeGroupOptions.find(o => o.stoppageType === params.group)?.groupColor!;
              const contrast = getContrastTextColor(groupColor);
              return (
                <div key={params.key}>
                  <div className="px-3 py-1 font-semibold text-sm text-white"
                       style={{backgroundColor: groupColor, color: contrast}}
                  >
                    {params.group}
                  </div>
                  <ul className="bg-white border border-theme-border divide-y">{params.children}</ul>
                </div>
              );
            }}
            onChange={(e, newValue) => {
              setFilterState(prev => ({
                ...prev,
                stoppageSubTypeId: newValue?.value ?? 0,
                stoppageTypeId: newValue?.downtimeTypeId ?? 0
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Stoppage Type" fullWidth/>
            )}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block font-medium mb-1">Time In</label>
            <TimePicker
              initialDate={filterState.timeIn}
              onChange={(e) => setFilterState(prev => ({...prev, timeIn: onShiftDate(e) ?? prev.timeIn}))}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
            />
          </div>
          <div className="flex-1">
            <label className="block font-medium mb-1">Time Out</label>
            <TimePicker
              initialDate={filterState.timeOut}
              onChange={(e) => setFilterState(prev => ({...prev, timeOut: onShiftDate(e)}))}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
              clearable={true}
            />
          </div>
        </div>
      </div>

      <div className="flex-1">
        <label className="block font-medium mb-1">Job Types</label>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={plantJobTypes}
          getOptionLabel={(option) => option.label}
          value={filterState.jobTypes}
          size="small"
          classes={{root: 'w-full'}}
          onChange={(_, newValue) => setFilterState(prev => ({...prev, jobTypes: newValue}))}
          renderInput={(params) => <TextField {...params} label="Job Types"/>}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-medium">Select Machines</label>
          <button
            type="button"
            className="text-sm text-theme-info hover:underline"
            onClick={() => {
              hasChosenMachines.current = true;
              setFilterState(prev => {
                const allIds = plants.map(p => p.id);
                const allSelected = allIds.every(id => prev.machineIds.includes(id));
                return {
                  ...prev,
                  machineIds: allSelected ? [] : allIds
                };
              });
            }}
          >
            {plants.every(p => filterState.machineIds.includes(p.id)) ? "Deselect All" : "Select All"}
          </button>
        </div>

        <div className="max-h-40 overflow-y-auto border rounded p-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {plants.map(item => (
              <label key={item.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterState.machineIds.includes(item.id)}
                  onChange={() => toggleSelection(item.id)}
                />
                <span>{item.plantRef}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button type="submit" variant="info" isLoading={isLoading} onClick={addMultipleStoppages}>
          Add
        </Button>
      </div>
    </form>
  );
};

export default MultipleStoppagesModal;
